import i18n, { type BackendModule, type ResourceKey } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';

/**
 * Each language is its own chunk, fetched when it is used: a visitor reads one,
 * and the two together were ~250 KB of the first script every page ran (the
 * public form included). `i18nReady` resolves once the visitor's language is
 * in; the entrypoints render after it, so no screen ever shows a raw key.
 */
const LOCALES: Record<string, () => Promise<ResourceKey>> = {
  en: () => import('./locales/en').then((m) => m.en),
  fr: () => import('./locales/fr').then((m) => m.fr),
};

const lazyLocales: BackendModule = {
  type: 'backend',
  init() {
    // Nothing to set up: each language is read on demand.
  },
  read(language, _namespace, callback) {
    const load = LOCALES[language];
    if (!load) {
      callback(null, {});
      return;
    }
    load().then(
      (translation) => callback(null, translation),
      (error: Error) => callback(error, null),
    );
  },
};

export const i18nReady = i18n
  .use(lazyLocales)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    // French is complete (locales.test.ts holds it to every English key), so a
    // French reader never downloads English too; anything else falls back to it.
    fallbackLng: (code) => (code?.startsWith('fr') ? [] : ['en']),
    supportedLngs: ['en', 'fr'],
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: 'i18n_lang',
      caches: ['localStorage'],
    },
    interpolation: {
      escapeValue: false,
    },
    react: { useSuspense: false },
  });

// Keep <html lang> honest for screen readers and spell-checkers when the user
// switches language (the server renders the initial value).
const syncDocumentLang = (lng: string) => {
  if (typeof document !== 'undefined') document.documentElement.lang = lng;
};
i18n.on('initialized', () => syncDocumentLang(i18n.language));
i18n.on('languageChanged', syncDocumentLang);

export default i18n;
